/**
 * Áreas para o filtro dos "Últimos focos": camadas de POLÍGONOS do módulo de
 * vetores (pontos e linhas não delimitam área) e a busca por nome.
 *
 * Os nomes vêm de `public/data/busca.json` (~100 KB, gerado por
 * `scripts/data/search_index.py`), baixado só quando o campo de busca recebe
 * foco. A geometria de uma área só é baixada quando ela é escolhida
 * (`areaGeometry.ts`). A busca em si é pura (testada em areaSearch.test.ts).
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import type { DataLayerId } from '../vectors/types';

/** Camadas de dados que podem filtrar focos (todas de polígonos). */
export type AreaLayerId = Extract<
  DataLayerId,
  | 'municipios'
  | 'estados'
  | 'amazonia_legal'
  | 'mesorregioes'
  | 'microrregioes'
  | 'regioes_intermediarias'
  | 'regioes_imediatas'
  | 'regioes_integracao_pa'
  | 'terras_indigenas'
  | 'unidades_conservacao'
>;

export interface AreaKindDef {
  id: AreaLayerId;
  /** Código de 1 letra no link compartilhável (chave `a`). */
  code: string;
  /** Nome do tipo de área, no singular. */
  label: string;
  /** Propriedade da feição que identifica a área (a mesma do índice de busca). */
  keyField: string;
  /** Siglas e sinônimos que também encontram o tipo na busca (ex.: "TI"). */
  aliases?: string;
}

/** Na ordem de desempate da busca (tipos mais usados primeiro). */
export const AREA_KINDS: readonly AreaKindDef[] = [
  { id: 'estados', code: 'e', label: 'Estado', keyField: 'sigla', aliases: 'uf' },
  { id: 'municipios', code: 'm', label: 'Município', keyField: 'cd_mun', aliases: 'cidade' },
  { id: 'terras_indigenas', code: 't', label: 'Terra indígena', keyField: 'nome', aliases: 'ti' },
  { id: 'unidades_conservacao', code: 'u', label: 'Unidade de conservação', keyField: 'nome', aliases: 'uc' },
  { id: 'amazonia_legal', code: 'l', label: 'Limite', keyField: 'nome' },
  { id: 'regioes_integracao_pa', code: 'a', label: 'Região de Integração (PA)', keyField: 'cd' },
  { id: 'mesorregioes', code: 'o', label: 'Mesorregião', keyField: 'cd' },
  { id: 'regioes_intermediarias', code: 'n', label: 'Região intermediária', keyField: 'cd' },
  { id: 'microrregioes', code: 'i', label: 'Microrregião', keyField: 'cd' },
  { id: 'regioes_imediatas', code: 'd', label: 'Região imediata', keyField: 'cd' },
];

const KIND_BY_ID = new Map(AREA_KINDS.map((k) => [k.id, k]));
const KIND_BY_CODE = new Map(AREA_KINDS.map((k) => [k.code, k]));

export function areaKind(id: AreaLayerId): AreaKindDef {
  return KIND_BY_ID.get(id)!;
}

export function areaKindByCode(code: string): AreaKindDef | undefined {
  return KIND_BY_CODE.get(code);
}

/** Uma área escolhida (ou sugerida). */
export interface AreaRef {
  layer: AreaLayerId;
  key: string;
  /** Nome para exibir (null enquanto o índice não carregou — ex.: vindo do link). */
  name: string | null;
  detail: string;
}

export const areaId = (a: Pick<AreaRef, 'layer' | 'key'>): string => `${a.layer}:${a.key}`;

// ---------------------------------------------------------------------------
// Índice de busca
// ---------------------------------------------------------------------------

/** Forma normalizada para comparar: sem acentos, minúsculas, só letras/dígitos separados por espaço. */
export function normalizeSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export interface AreaEntry extends AreaRef {
  name: string;
  /** Nome normalizado. */
  norm: string;
  /** Palavras de nome + detalhe + tipo (normalizadas), para buscas como "ti kayapo". */
  words: string[];
  /** Posição do tipo em AREA_KINDS (desempate). */
  rank: number;
}

export interface AreaIndex {
  entries: AreaEntry[];
  byId: Map<string, AreaEntry>;
}

/** Monta o índice a partir do JSON de `busca.json` (tolerante a camadas desconhecidas e itens ruins). */
export function buildAreaIndex(json: unknown): AreaIndex {
  const entries: AreaEntry[] = [];
  const layers = (json as { layers?: unknown } | null)?.layers;
  if (layers && typeof layers === 'object') {
    AREA_KINDS.forEach((kind, rank) => {
      const items = (layers as Record<string, unknown>)[kind.id];
      if (!Array.isArray(items)) return;
      const kindWords = normalizeSearch(`${kind.label} ${kind.aliases ?? ''}`);
      for (const it of items) {
        if (!Array.isArray(it) || typeof it[0] !== 'string' || typeof it[1] !== 'string' || !it[0] || !it[1]) continue;
        const detail = typeof it[2] === 'string' ? it[2] : '';
        const norm = normalizeSearch(it[1]);
        entries.push({
          layer: kind.id,
          key: it[0],
          name: it[1],
          detail,
          norm,
          words: `${norm} ${normalizeSearch(detail)} ${kindWords}`.split(' ').filter(Boolean),
          rank,
        });
      }
    });
  }
  return { entries, byId: new Map(entries.map((e) => [areaId(e), e])) };
}

/**
 * Sugestões para o texto digitado, melhores primeiro:
 * nome igual › nome começa com o texto › alguma palavra do nome começa com o
 * texto › todas as palavras digitadas começam palavras do nome/UF/tipo ›
 * texto no meio do nome. Empates: tipo (estados, municípios, TIs, UCs…),
 * nome mais curto, ordem alfabética.
 */
export function searchAreas(
  index: AreaIndex,
  query: string,
  opts: { limit?: number; exclude?: ReadonlySet<string> } = {},
): AreaEntry[] {
  const q = normalizeSearch(query);
  if (!q) return [];
  const limit = opts.limit ?? 8;
  const tokens = q.split(' ');
  const scored: { e: AreaEntry; score: number }[] = [];
  for (const e of index.entries) {
    if (opts.exclude?.has(areaId(e))) continue;
    let score: number;
    if (e.norm === q) score = 0;
    else if (e.norm.startsWith(q)) score = 1;
    else if (e.norm.includes(` ${q}`)) score = 2;
    else if (tokens.every((t) => e.words.some((w) => w.startsWith(t)))) score = 3;
    else if (q.length >= 3 && e.norm.includes(q)) score = 4;
    else continue;
    scored.push({ e, score });
  }
  scored.sort(
    (a, b) =>
      a.score - b.score || a.e.rank - b.e.rank || a.e.name.length - b.e.name.length || a.e.name.localeCompare(b.e.name, 'pt-BR'),
  );
  return scored.slice(0, limit).map((s) => s.e);
}

let indexPromise: Promise<AreaIndex> | null = null;
let loadedIndex: AreaIndex | null = null;

/** Índice já carregado (ou null). */
export function getLoadedAreaIndex(): AreaIndex | null {
  return loadedIndex;
}

/** Baixa (uma vez) o índice de nomes. Em caso de erro, uma nova chamada tenta de novo. */
export function loadAreaIndex(): Promise<AreaIndex> {
  if (indexPromise) return indexPromise;
  const url = `${import.meta.env.BASE_URL}data/busca.json`;
  indexPromise = fetchWithRetry(url, {}, { retries: 2, read: (r) => r.json() as Promise<unknown> })
    .then((json) => (loadedIndex = buildAreaIndex(json)))
    .catch((err: unknown) => {
      indexPromise = null;
      throw err;
    });
  return indexPromise;
}
